import { useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ZodType } from "zod";
import { apiFetch } from "@/lib/api";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { DetailsDrawer } from "@/components/details-drawer";
import { EntityForm } from "@/components/entity-form";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { UseFormReturn, FieldValues, DefaultValues } from "react-hook-form";

interface Paged<T> { items: T[]; page: number; pageSize: number; totalCount: number; }

export function LookupListPage<TRow extends { deleted: boolean }, TForm extends FieldValues>({
  title, apiPath, requiredPermission, columns, rowKey, schema,
  toFormValues, toApiBody, blankFormValues, fields,
}: {
  title: string;
  apiPath: string;
  requiredPermission?: string;
  columns: readonly DataGridColumn<TRow>[];
  rowKey: (r: TRow) => string | number;
  schema: ZodType<TForm, TForm>;
  toFormValues: (row: TRow) => DefaultValues<TForm>;
  toApiBody: (form: TForm) => unknown;
  blankFormValues: DefaultValues<TForm>;
  fields: (form: UseFormReturn<TForm>) => ReactNode;
}) {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState<TRow | null>(null);
  const [creating, setCreating] = useState(false);
  void requiredPermission;

  const list = useQuery({
    queryKey: [apiPath, page, includeDeleted],
    queryFn: () => apiFetch<Paged<TRow>>(`${apiPath}?page=${page}&pageSize=50${includeDeleted ? "" : "&deleted=false"}`),
  });

  const upsert = useMutation({
    mutationFn: (form: TForm) => editing
      ? apiFetch<TRow>(`${apiPath}/${rowKey(editing)}`, { method: "PATCH", body: JSON.stringify(toApiBody(form)) })
      : apiFetch<TRow>(apiPath,                            { method: "POST",  body: JSON.stringify(toApiBody(form)) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [apiPath] }); setEditing(null); setCreating(false); },
  });

  const enrichedColumns: DataGridColumn<TRow>[] = [
    ...columns,
    { id: "deleted", header: "Status", accessor: (r) => r.deleted ? "(deleted)" : "Active" },
  ];

  return (
    <ListPage
      title={title}
      primaryAction={<IntentButton onClick={() => setCreating(true)}>New</IntentButton>}
      filterBar={<div className="flex items-center gap-2"><Switch id="d" checked={includeDeleted} onCheckedChange={setIncludeDeleted} /><Label htmlFor="d">Show deleted</Label></div>}>
      <DataGrid rows={list.data?.items ?? []} columns={enrichedColumns} rowKey={rowKey}
        totalRows={list.data?.totalCount ?? 0} pageIndex={page} pageSize={50}
        loading={list.isLoading} onPageChange={setPage} />
      <DetailsDrawer
        open={creating || editing !== null}
        onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}
        title={editing ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}>
        <EntityForm
          schema={schema}
          defaultValues={editing ? toFormValues(editing) : blankFormValues}
          onSubmit={(v) => upsert.mutate(v)}>
          {(form) => fields(form)}
        </EntityForm>
      </DetailsDrawer>
    </ListPage>
  );
}
